<template>
    <Authenticated>
        <template #header>
            <div class="mb-7">
                <PageTitle translationKey="opportunities.overview.page-title"/>
                <OpportunitiesChart />
            </div>
            <Tabs @switchTab="switchTab" :tabs="tabs" classes="hidden lg:flex"/>
            <DropdownTabs @switchTab="switchTab" :activeTab="getActiveTab()" :tabs="tabs" classes="block lg:hidden"/>
        </template>
        <div class="flex flex-col lg:flex-row mt-10 lg:mt-6">
            <TableLarge v-if="getActiveTab() === 'all-opportunities'" :opportunities="filteredOpportunities" :show-more="false"/>
        </div>
    </Authenticated>
</template>

<script>
import PageTitle from "@/Components/PageTitle";
import Tabs from "@/Components/Tabs";
import BarChart from "@/Components/Charts/Bar";
import {Inertia} from '@inertiajs/inertia'
import TableLarge from '@/Components/Opportunities/Table/TableLarge';
import DropdownTabs from '@/Components/DropdownTabs';
import Authenticated from '@/Layouts/Authenticated';
import VueTailwindDatepicker from 'vue-tailwind-datepicker';
import moment from 'moment';
import Multiselect from '@vueform/multiselect'
import { ref } from 'vue'
import OpportunitiesChart from "@/Components/OpportunitiesChart";
const _ = require('lodash');

export default {
    data: () => ({
        tabs: {},
        formatter: ref({
            date: 'DD MMM YYYY',
            month: 'MMM'
        }),
        dateRange: ref([]),
        allOpportunities: [],
        filteredOpportunities: [],
        statusFilterValue: null,
        statuses: [],
    }),
    props: {
        opportunities: Object,
        preselected_start_date: String,
        preselected_end_date: String,
    },
    components: {
        OpportunitiesChart,
        Authenticated,
        TableLarge,
        Tabs,
        PageTitle,
        DropdownTabs,
        BarChart,
        VueTailwindDatepicker,
        Multiselect,
    },
    methods: {
        switchTab(key) {
            if (this.tabs[key].route) {
                Inertia.visit(route(this.tabs[key].route));
                return;
            }

            Object.keys(this.tabs).forEach(key => this.tabs[key].active = false);
            this.tabs[key].active = true;
        },
        getActiveTab() {
            return Object.keys(this.tabs).find(key => this.tabs[key].active === true);
        },
        setDateRange(dateRange) {
            if (!dateRange[0].isValid() || !dateRange[1].isValid()) {
                return;
            }

            this.dateRange = [ dateRange[0].format('DD MMM YYYY'), dateRange[1].format('DD MMM YYYY') ];
        },
        setFilteredStatuses() {
            const currentUrl = new URL(window.location.href);
            let filteredStatusesFromParams = [];

            this.statuses.forEach(status => {
                if (currentUrl.searchParams.get('status_' + _.toLower(status)) != null) {
                    filteredStatusesFromParams.push(status)
                }
            });

            this.statusFilterValue = filteredStatusesFromParams;
        },
        onDateSelect(dateRange) {
            const currentUrl = new URL(window.location.href);

            if (dateRange[0] == null || dateRange[1] == null) {
                currentUrl.searchParams.delete('startDate');
                currentUrl.searchParams.delete('endDate');
                history.pushState({}, null, currentUrl);
                this.filteredOpportunities = this.allOpportunities;
                return;
            }

            currentUrl.searchParams.set('startDate', moment(dateRange[0], 'DD MMM YYYY').format('DD-MM-YYYY'));
            currentUrl.searchParams.set('endDate', moment(dateRange[1], 'DD MMM YYYY').format('DD-MM-YYYY'));
            history.pushState({}, null, currentUrl);
            this.filteredOpportunities = this.getFilteredOpportunities();
        },
        onStatusFilterChange(statuses) {
            const currentUrl = new URL(window.location.href);

            this.statuses.filter(status => {
                currentUrl.searchParams.delete('status_' + _.toLower(status) );
            });
            this.filteredOpportunities = this.allOpportunities;

            statuses.filter(status => {
                currentUrl.searchParams.set('status_' + _.toLower(status), true);
            });

            history.pushState({}, null, currentUrl);
            this.filteredOpportunities = this.getFilteredOpportunities();
        },
        getFilteredOpportunities() {
            return this.allOpportunities.filter(opportunity => {
                let isWithinDateRange = true;
                let isWithinFilteredStatuses = true;

                if (this.dateRange.length >= 2) {
                    const opportunityCreatedDate = moment (opportunity.createdDate, 'DD-MM-YYYY');
                    const datePickerStartDate    = moment( this.dateRange[0], 'DD MMM YYYY' );
                    const datePickerEndDate      = moment( this.dateRange[1], 'DD MMM YYYY' );

                    isWithinDateRange = opportunityCreatedDate.isAfter(datePickerStartDate) && opportunityCreatedDate.isBefore(datePickerEndDate);
                }

                if (this.statusFilterValue?.length > 0) {
                    isWithinFilteredStatuses = this.statusFilterValue.map(status => {
                        return _.toLower(status);
                    }).includes( opportunity.status );
                }

                if (isWithinDateRange && isWithinFilteredStatuses) {
                    return true;
                }
            });
        },
        getColorClassByStatus(status) {
            switch (status) {
                case 'Open':
                    return 'bg-blue-100';
                case 'Won':
                    return 'bg-green-100';
                case 'Lost':
                    return 'bg-red-100';
                default:
                    return 'bg-blue-100';
            }
        }
    },
    created() {
        this.statuses = [...new Set(this.opportunities.map(({status}) => _.capitalize(status)))];
        this.tabs = {
            "all-opportunities": {
                translationKey: "opportunities.overview.all-opportunities",
                active: true,
            },
            "create-opportunity": {
                translationKey: "opportunities.overview.add-opportunity",
                active: false,
                route: this.$page.props.locale + '.opportunities.create'
            }
        }
        this.filteredOpportunities = this.allOpportunities = this.opportunities;
        this.setDateRange( [
            moment(this.preselected_start_date, 'DD-MM-YYYY'),
            moment(this.preselected_end_date, 'DD-MM-YYYY')
        ] );
        this.setFilteredStatuses();
    },
    watch: {
        dateRange(newValue) {
            this.onDateSelect(newValue);
        },
        statusFilterValue(newValue) {
            this.onStatusFilterChange(newValue);
        },
    },
}
</script>

<style lang="css">
@import '@vueform/multiselect/themes/default.css';
</style>
